import { ChangeEvent, memo, useState, FC, useEffect, useRef } from "react";
import config from "../../../config.json";
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useLocation } from "react-router-dom";

import { PrimaryButton } from "../../atoms/button/PrimaryButton";
import { useConfirm } from "../../../hooks/useConfirm";
import { HeaderLayout } from "../../templates/HeaderLayout";
import { useLoginUser } from "../../../hooks/useLoginUser";
import { useMessage } from "../../../hooks/useMessage";

export const ChangePassword: FC = memo(() => {
  const location = useLocation();
  const { confirmPassword, loading } = useConfirm();
  const { loginUser } = useLoginUser();
  const { showMessage } = useMessage();

  // メッセージ表示フラグをuseRefで管理
  const messageShownRef = useRef(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // パスワード有効期限チェックからの遷移かどうか
  const isExpiredPassword = (location.state as any)?.expiredPassword || false;
  const isForceChange = (location.state as any)?.forceChange || false;

  // 画面表示時に期限切れメッセージを表示（初回のみ）
  useEffect(() => {
    if (!messageShownRef.current) {
      console.log(
        "DEBUG: ChangePassword showing message, isExpiredPassword:",
        isExpiredPassword,
        "isForceChange:",
        isForceChange
      );

      if (isExpiredPassword) {
        showMessage({
          title: "パスワードの有効期限が切れています",
          description: "新しいパスワードを設定してください。",
          status: "warning",
        });
        messageShownRef.current = true;
      } else if (isForceChange) {
        showMessage({
          title: "パスワードの変更が必要です",
          description: "新しいパスワードを設定してください。",
          status: "warning",
        });
        messageShownRef.current = true;
      }
    }
  }, [isExpiredPassword, isForceChange, showMessage]); // パスワード状態を依存配列から除去

  // コンポーネントのアンマウント時にフラグをリセット
  useEffect(() => {
    return () => {
      // コンポーネントがアンマウントされる時にフラグをリセット
      messageShownRef.current = false;
    };
  }, []);

  const onChangeCurrentPassword = (e: ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(e.target.value);
  };

  const onChangeNewPassword = (e: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
  };

  const onChangeConfirmNewPassword = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmNewPassword(e.target.value);
  };

  const onClickChangePassword = () => {
    // バリデーション
    if (currentPassword === "") {
      showMessage({
        title: "現在のパスワードを入力してください",
        status: "error",
      });
      return;
    }
    if (newPassword === "") {
      showMessage({
        title: "新しいパスワードを入力してください",
        status: "error",
      });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      showMessage({
        title: "新しいパスワードと確認用パスワードが一致しません",
        status: "error",
      });
      return;
    }
    if (newPassword.length < config.PASSWORD_MIN_LENGTH) {
      const message = `パスワードは${config.PASSWORD_MIN_LENGTH}文字以上で入力してください`;
      showMessage({ title: message, status: "error" });
      return;
    }
    if (config.REQUIRE_UPPERCASE && !/[A-Z]/.test(newPassword)) {
      showMessage({
        title: "パスワードに大文字を含めてください",
        status: "error",
      });
      return;
    }
    if (config.REQUIRE_LOWERCASE && !/[a-z]/.test(newPassword)) {
      showMessage({
        title: "パスワードに小文字を含めてください",
        status: "error",
      });
      return;
    }
    if (config.REQUIRE_NUMBERS && !/[0-9]/.test(newPassword)) {
      showMessage({
        title: "パスワードに数字を含めてください",
        status: "error",
      });
      return;
    }
    if (
      config.REQUIRE_SYMBOLS &&
      !/[!@#$%^&*(),.?\":{}|<>]/.test(newPassword)
    ) {
      showMessage({
        title: "パスワードに記号を含めてください",
        status: "error",
      });
      return;
    }
    if (currentPassword === newPassword) {
      showMessage({
        title: "現在のパスワードと同じパスワードは使用できません",
        status: "error",
      });
      return;
    }

    // パスワード変更実行
    if (loginUser?.userName) {
      try {
        confirmPassword(loginUser.userName, currentPassword, newPassword);
      } catch (err: any) {
        let errorMessage = "";
        if (err?.message?.includes("Attempt limit exceeded")) {
          errorMessage =
            "パスワード変更の試行回数制限を超えました。しばらくしてから再試行してください。";
        } else {
          errorMessage = err?.message || "パスワード変更に失敗しました";
        }
        showMessage({ title: errorMessage, status: "error" });
      }
    }
  };

  return (
    <HeaderLayout>
      <Flex align="center" justify="center" mt={4}>
        <Box bg="white" w="md" p={6} borderRadius="md" shadow="md">
          <Heading textAlign="center" mb={4}>
            パスワード変更
          </Heading>

          <Stack spacing={4}>
            <Box>
              <FormLabel>現在のパスワード</FormLabel>
              <Input
                type="password"
                placeholder="現在のパスワード"
                value={currentPassword}
                onChange={onChangeCurrentPassword}
              />
            </Box>

            <Box>
              <FormLabel>新しいパスワード</FormLabel>
              <Input
                type="password"
                placeholder="新しいパスワード"
                value={newPassword}
                onChange={onChangeNewPassword}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {`${config.PASSWORD_MIN_LENGTH}文字以上`}
                {config.REQUIRE_UPPERCASE ? "・大文字" : ""}
                {config.REQUIRE_LOWERCASE ? "・小文字" : ""}
                {config.REQUIRE_NUMBERS ? "・数字" : ""}
                {config.REQUIRE_SYMBOLS ? "・記号" : ""}
                を含めてください
              </Text>
            </Box>

            <Box>
              <FormLabel>新しいパスワード（確認）</FormLabel>
              <Input
                type="password"
                placeholder="新しいパスワード（確認）"
                value={confirmNewPassword}
                onChange={onChangeConfirmNewPassword}
              />
            </Box>

            <Stack pt={4} spacing={3}>
              <PrimaryButton
                disabled={
                  currentPassword === "" ||
                  newPassword === "" ||
                  confirmNewPassword === ""
                }
                isLoading={loading}
                onClick={onClickChangePassword}
              >
                パスワードを変更
              </PrimaryButton>
            </Stack>
          </Stack>
        </Box>
      </Flex>
    </HeaderLayout>
  );
});
